import { Menu } from '@affine/component';
import { NotificationCountService } from '@affine/core/modules/notification';
import { track } from '@affine/track';
import { useService } from '@toeverything/infra';
import {
  cloneElement,
  type HTMLAttributes,
  type ReactElement,
  type Ref,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import { STREAMLAIN_DROPDOWN_MENU_OFFSET } from '../dropdown-panel-chrome/chrome';
import { RadixDropdownPanelChrome } from '../dropdown-panel-chrome/radix-chrome';
import { NotificationList } from './list';
import * as styles from './popover.style.css';

/** Shared ordinary inbox state; only its selected trigger displays the menu. */
export function useNotificationPopoverController() {
  const notificationCountService = useService(NotificationCountService);
  const selectedTrigger = useRef<HTMLElement | null>(null);
  const [state, setState] = useState({
    open: false,
    trigger: null as HTMLElement | null,
  });
  const selectTrigger = useCallback((element: HTMLElement) => {
    selectedTrigger.current = element;
  }, []);
  const registerTrigger = useCallback((element: HTMLElement) => {
    if (!selectedTrigger.current?.isConnected)
      selectedTrigger.current = element;
    return () => {
      if (selectedTrigger.current === element) selectedTrigger.current = null;
    };
  }, []);
  const onOpenChange = useCallback(
    (open: boolean) => {
      const trigger = selectedTrigger.current;
      if (open && !trigger?.isConnected) return;
      if (open) {
        track.$.sidebar.notifications.openInbox({
          unreadCount: notificationCountService.count$.value,
        });
      }
      setState({ open, trigger });
    },
    [notificationCountService]
  );
  const activate = useCallback(() => {
    onOpenChange(!state.open || selectedTrigger.current !== state.trigger);
  }, [onOpenChange, state]);
  return { ...state, selectTrigger, registerTrigger, onOpenChange, activate };
}
export type NotificationPopoverController = ReturnType<
  typeof useNotificationPopoverController
>;

/**
 * The one notification inbox popover: opens the NotificationList to the right
 * of the given trigger with the StreamLain dropdown panel chrome (tail
 * pointing at the trigger, grow-out-of-the-tail animation) and tracks inbox
 * opens. The trigger element receives the popover trigger ref.
 */
export const NotificationListPopover = ({
  children,
  controller: sharedController,
}: {
  controller?: NotificationPopoverController;
  children: (
    open: boolean
  ) => ReactElement<HTMLAttributes<HTMLElement> & { ref?: Ref<HTMLElement> }>;
}) => {
  const triggerRef = useRef<HTMLElement | null>(null);
  const localController = useNotificationPopoverController();
  const controller = sharedController ?? localController;
  const open = controller.open && controller.trigger === triggerRef.current;
  const { registerTrigger } = controller;
  useLayoutEffect(() => {
    if (triggerRef.current) return registerTrigger(triggerRef.current);
    return;
  }, [registerTrigger]);
  const trigger = children(open);

  return (
    <Menu
      rootOptions={{
        open,
        onOpenChange: controller.onOpenChange,
      }}
      contentOptions={{
        side: 'right',
        align: 'start',
        sideOffset: STREAMLAIN_DROPDOWN_MENU_OFFSET,
        alignOffset: -8,
        collisionPadding: 8,
        className: styles.popover,
        // A press on the trigger of the open inbox is its toggle. Closing
        // here would let the click that follows open it again.
        onInteractOutside: event => {
          if (
            sharedController &&
            event.target instanceof Node &&
            triggerRef.current?.contains(event.target)
          )
            event.preventDefault();
        },
      }}
      items={
        <RadixDropdownPanelChrome triggerRef={triggerRef}>
          <NotificationList />
        </RadixDropdownPanelChrome>
      }
    >
      {cloneElement(trigger, {
        ref: triggerRef,
        onClickCapture: event => {
          trigger.props.onClickCapture?.(event);
          if (!event.defaultPrevented)
            controller.selectTrigger(event.currentTarget);
        },
        onPointerDown: event => {
          trigger.props.onPointerDown?.(event);
          // Shared sidebar entries activate on the real click, including AI clicks.
          if (sharedController && event.button === 0 && !event.ctrlKey)
            event.preventDefault();
        },
        onKeyDown: event => {
          trigger.props.onKeyDown?.(event);
          if (
            sharedController &&
            !event.defaultPrevented &&
            (event.key === 'Enter' || event.key === ' ')
          ) {
            event.preventDefault();
            event.currentTarget.click();
          } else if (
            sharedController &&
            !event.defaultPrevented &&
            event.key === 'ArrowDown'
          ) {
            event.preventDefault();
            controller.selectTrigger(event.currentTarget);
            controller.onOpenChange(true);
          }
        },
      })}
    </Menu>
  );
};
